/**
 * cardLinkingApi.js
 * 
 * Module to interact with Schumann Card Linking API
 * Handles card checking, linking, and discount code management
 * 
 * API Spec: Schumann Card API v1.0
 * - Base URL: /card-check and /card-link (via Shopify proxy)
 * - Authentication: Automatic via Shopify headers
 * - Rate Limit: 20 requests per 5 minutes per IP
 * 
 * Usage:
 *   await CardLinkingApi.checkCard('ABC123DEF456')
 *   await CardLinkingApi.linkCard('ABC123DEF456', customerId)
 *   await CardLinkingApi.applyDiscountCode('SCHM-ABC123')
 */

window.CardLinkingApi = (() => {
  // Configuration
  const config = {
    baseUrl: window.Shopify?.routes?.root || '/',
    endpoints: {
      cardCheck: 'apps/api/card/check',
      cardLink: 'apps/api/card/link'
    },
    rateLimit: {
      maxRequests: 20,
      windowMs: 5 * 60 * 1000 // 5 minutes
    }
  };

  /**
   * Make API request with error handling
   * @param {string} endpoint - The endpoint path
   * @param {object} params - Query parameters
   * @returns {Promise} Response data
   */
  async function makeRequest(endpoint, params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${config.baseUrl}${endpoint}?${queryString}`;
      
      console.log(`[CardLinkingApi] → Fetching: ${endpoint}`);
      console.log(`[CardLinkingApi] URL:`, url);
      console.log(`[CardLinkingApi] Params:`, params);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log(`[CardLinkingApi] Response status:`, response.status);
      
      // Handle rate limiting (429)
      if (response.status === 429) {
        const retryAfter = parseInt(response.headers.get('Retry-After'), 10) || 60;
        console.warn(`[CardLinkingApi] ⚠️ Rate limited (429), retry after ${retryAfter}s`);
        throw {
          code: 'RATE_LIMIT_EXCEEDED',
          message: `Rate limited. Please try again in ${retryAfter} seconds.`,
          retryAfter: retryAfter,
          statusCode: 429
        };
      }

      // Always parse response as JSON first (both success and error responses are JSON)
      let data = await response.json();
      console.log(`[CardLinkingApi] Response status:`, response.status);
      console.log(`[CardLinkingApi] Response data:`, data);

      // Handle API-level errors (check before HTTP status)
      if (!data.success) {
        console.warn(`[CardLinkingApi] ⚠️ API returned success:false`);
        console.error(`[CardLinkingApi] API Error:`, data.error, `Code: ${data.code}`);
        
        // Special handling for NOT_LOGGED_IN - preserve redirectUrl
        if (data.code === 'NOT_LOGGED_IN' && data.redirectUrl) {
          console.log(`[CardLinkingApi] 🔐 NOT_LOGGED_IN with redirectUrl:`, data.redirectUrl);
          throw {
            code: 'NOT_LOGGED_IN',
            message: data.error || 'Customer not logged in',
            redirectUrl: data.redirectUrl,
            statusCode: 401
          };
        }
        
        throw {
          code: data.code || 'UNKNOWN_ERROR',
          message: data.error || 'An unknown error occurred',
          statusCode: response.status
        };
      }

      // Handle HTTP errors only after checking for API errors
      if (!response.ok) {
        console.error(`[CardLinkingApi] ❌ HTTP error: ${response.status}`);
        throw {
          code: data.code || `HTTP_${response.status}`,
          message: data.error || `HTTP Error: ${response.status}`,
          statusCode: response.status
        };
      }

      // Success response
      console.log(`[CardLinkingApi] ✅ Response data:`, data);

      return data;
    } catch (error) {
      // Network errors or other exceptions
      if (error.code) {
        throw error; // Already formatted
      }
      throw {
        code: 'NETWORK_ERROR',
        message: error.message || 'Network error occurred',
        statusCode: 0
      };
    }
  }

   /**
    * Check if a card is linked to the customer's account
    * Also returns existing linked cards to show replacement warning
    * @param {string} cardUid - The card UID (typically from NFC scan)
    * @param {string} customerId - Optional customer ID to enable replacement warning
    * @returns {Promise<object>} Card status, discount code, and existing linked cards
    * 
    * Response on success (card not yet linked):
    * {
    *   success: true,
    *   isLinked: false,
    *   cardStatus: "ACTIVE",
    *   discountCode: null,
    *   linkedCards: [
    *     {
    *       id: "card-uuid",
    *       cardUid: "EXISTING_CARD_123",
    *       status: "ACTIVE",
    *       linkedAt: "2026-01-15T10:30:00.000Z",
    *       discountCode: "SCHUMANN_ABC123"
    *     }
    *   ]
    * }
    * 
    * Response on success (card already linked):
    * {
    *   success: true,
    *   isLinked: true,
    *   cardStatus: "ACTIVE",
    *   discountCode: "SCHM-ABC123"
    * }
    * 
    * Response on error:
    * {
    *   success: false,
    *   error: "Card not found",
    *   code: "CARD_NOT_FOUND"
    * }
    */
   async function checkCard(cardUid, customerId = null) {
     console.log(`[CardLinkingApi.checkCard] Called with cardUid:`, cardUid);
     
     if (!cardUid) {
       console.error(`[CardLinkingApi.checkCard] ❌ Card UID is required`);
       throw {
         code: 'INVALID_PARAMS',
         message: 'Card UID is required',
         statusCode: 400
       };
     }

     const params = {
       cardUid: cardUid
     };

     // Pass customer ID if provided (enables replacement warning)
     if (customerId) {
       params.logged_in_customer_id = customerId;
       console.log(`[CardLinkingApi.checkCard] Customer ID provided - will show replacement warning if applicable`);
     }

     console.log(`[CardLinkingApi.checkCard] → Making request to ${config.endpoints.cardCheck}`);
     return makeRequest(config.endpoints.cardCheck, params);
   }

   /**
    * Link a card to customer account
    * @param {string} cardUid - The card UID from NFC scan
    * @param {string} customerId - Shopify customer ID (optional for logged-in users)
    * @returns {Promise<object>} Linked card data and discount code
    * 
    * Response on success:
    * {
    *   success: true,
    *   customerId: "gid://shopify/customer/123456789",
    *   discountCode: "SCHM-ABC123",
    *   cardId: "card_ABC123",
    *   programId: "prog_XYZ789"
    * }
    * 
    * Response if not logged in (401 Unauthorized):
    * {
    *   success: false,
    *   error: "Customer not logged in",
    *   code: "NOT_LOGGED_IN",
    *   redirectUrl: "https://shop.myshopify.com/customer_authentication/login?return_to=..."
    * }
    * 
    * Note: If customer is not logged in, the API returns a redirectUrl
    * which should be used to redirect the user to login.
    */
   async function linkCard(cardUid, customerId = null) {
     console.log(`[CardLinkingApi.linkCard] Called with:`, { cardUid, customerId });
     
     if (!cardUid) {
       console.error(`[CardLinkingApi.linkCard] ❌ Card UID is required`);
       throw {
         code: 'INVALID_PARAMS',
         message: 'Card UID is required',
         statusCode: 400
       };
     }

     const params = {
       cardUid: cardUid
     };

     // Pass customer ID via query parameters if provided
     if (customerId) {
       params.logged_in_customer_id = customerId;
       console.log(`[CardLinkingApi.linkCard] Customer ID provided, adding to params`);
     } else {
       console.log(`[CardLinkingApi.linkCard] No customer ID provided, will trigger login redirect if needed`);
     }

     console.log(`[CardLinkingApi.linkCard] → Making request to ${config.endpoints.cardLink}`);
     try {
       return await makeRequest(config.endpoints.cardLink, params);
     } catch (error) {
       // Check if this is a NOT_LOGGED_IN error with redirectUrl
       if (error.code === 'NOT_LOGGED_IN' && error.redirectUrl) {
         console.log(`[CardLinkingApi.linkCard] User not logged in, redirectUrl available`, error.redirectUrl);
         // Re-throw with redirectUrl included so modal can handle it
         throw error;
       }
       throw error;
     }
   }

  /**
   * Apply discount code to cart
   * @param {string} discountCode - Discount code from API response
   * @returns {Promise<object>} Cart update response
   */
  async function applyDiscountCode(discountCode) {
    if (!discountCode) {
      throw {
        code: 'INVALID_PARAMS',
        message: 'Discount code is required',
        statusCode: 400
      };
    }

    try {
      const response = await fetch(`${config.baseUrl}cart/update.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          discount: discountCode
        })
      });

      if (!response.ok) {
        throw {
          code: `HTTP_${response.status}`,
          message: `Failed to apply discount: HTTP ${response.status}`,
          statusCode: response.status
        };
      }

      return await response.json();
    } catch (error) {
      if (error.code) {
        throw error;
      }
      throw {
        code: 'NETWORK_ERROR',
        message: `Failed to apply discount: ${error.message}`,
        statusCode: 0
      };
    }
  }

  /**
   * Get customer ID from page context
   * Looks for customer ID in data attributes or global variables
   * @returns {string|null} Customer ID in format "gid://shopify/customer/..." or null
   */
  function getCustomerId() {
    // Method 1: Check data attribute on html element
    const customerId = document.documentElement.dataset.customerId;
    if (customerId) return customerId;

    // Method 2: Check window global
    if (window.__customerId) return window.__customerId;

    // Method 3: Check Liquid-rendered script tag (common pattern)
    const scriptEl = document.querySelector('script[data-customer-id]');
    if (scriptEl) return scriptEl.dataset.customerId;

    return null;
  }

  /**
   * Format card status for display
   * @param {string} cardStatus - Status from API
   * @returns {object} Formatted status object
   */
  function formatCardStatus(cardStatus) {
    const statusMap = {
      'PENDING_ACTIVATION': {
        display: 'Pending Activation',
        available: true,  // Can be linked after login
        color: '#ff9800'
      },
      'ACTIVE': {
        display: 'Active',
        available: true,
        color: '#4caf50'
      },
      'LOST': {
        display: 'Lost',
        available: false,
        color: '#f44336'
      },
      'REPLACED': {
        display: 'Replaced',
        available: false,
        color: '#757575'
      },
      'SUSPENDED': {
        display: 'Suspended',
        available: false,
        color: '#f44336'
      }
    };

    return statusMap[cardStatus] || {
      display: cardStatus,
      available: false,
      color: '#999'
    };
  }

  /**
   * Show error message using Toastify
   * @param {string} message - Error message
   * @param {number} duration - Duration in milliseconds (default 4000)
   */
  function showError(message, duration = 4000) {
    if (window.Toastify) {
      Toastify({
        text: message,
        duration: duration,
        gravity: 'bottom',
        position: 'left',
        style: {
          background: '#f44336',
          color: 'white'
        }
      }).showToast();
    } else {
      console.error('Error:', message);
    }
  }

  /**
   * Show success message using Toastify
   * @param {string} message - Success message
   * @param {number} duration - Duration in milliseconds (default 3000)
   */
  function showSuccess(message, duration = 3000) {
    if (window.Toastify) {
      Toastify({
        text: message,
        duration: duration,
        gravity: 'bottom',
        position: 'left',
        style: {
          background: '#4caf50',
          color: 'white'
        }
      }).showToast();
    } else {
      console.log('Success:', message);
    }
  }

  /**
   * Show info message using Toastify
   * @param {string} message - Info message
   * @param {number} duration - Duration in milliseconds (default 3000)
   */
  function showInfo(message, duration = 3000) {
    if (window.Toastify) {
      Toastify({
        text: message,
        duration: duration,
        gravity: 'bottom',
        position: 'left',
        style: {
          background: '#2196F3',
          color: 'white'
        }
      }).showToast();
    } else {
      console.log('Info:', message);
    }
  }

  /**
   * Handle API error with user-friendly message
   * @param {object} error - Error object from API
   */
  function handleError(error) {
    // Map error codes to user-friendly messages
    const errorMessages = {
      'INVALID_PARAMS': 'Invalid card UID. Please check and try again.',
      'UNAUTHORIZED': 'Authentication failed. Please log in again.',
      'STORE_NOT_FOUND': 'Store not configured. Please contact support.',
      'CARD_NOT_FOUND': 'Card not found in system.',
      'CARD_ALREADY_LINKED': 'This card is already linked to an account.',
      'CUSTOMER_NOT_FOUND': 'Customer not found. Please log in again.',
      'RATE_LIMIT_EXCEEDED': error.message || 'Too many requests. Please wait a moment.',
      'NETWORK_ERROR': 'Network error occurred. Please check your connection.',
      'INTERNAL_ERROR': 'Server error. Please try again later.'
    };

    const message = errorMessages[error.code] || error.message || 'An error occurred';
    showError(message);
    
    console.error('Card Linking API Error:', {
      code: error.code,
      message: error.message,
      statusCode: error.statusCode
    });
  }

  // Public API
  return {
    checkCard,
    linkCard,
    applyDiscountCode,
    getCustomerId,
    formatCardStatus,
    showError,
    showSuccess,
    showInfo,
    handleError,
    config // Expose config for testing/debugging
  };
})();
